import "./DocumentEditor.scss";
import Toolbar from "./Toolbar/Toolbar";
import {useDocumentEditor} from "../../hooks/useDocumentEditor";
import {EditorContent} from "@tiptap/react";
import {useEffect, useRef, useState} from "react";
import Comments from "../comments/Comments";
import TableModal from "./tableModal/TableModal";
import debounce from "lodash/debounce";
import {cleanDiffDocument, getChangedNodes, getMergedDoc, handleSelectionChange} from "../../utils/Utils";
import DocumentActions from "../../api/DocumentActions";
import useVariables from "../../contexts/variablesContext/useVariables";
import useCheckpointsList from "../../contexts/checkpointsListContext/useCheckpointsList";
import Checkpoints from "./checkpoints/Checkpoints";
import {TextSelection} from "prosemirror-state";

const DocumentEditor = ({formik}) => {
    const {variables} = useVariables();
    const {setCheckpointsList} = useCheckpointsList();
    const checkpointIdRef = useRef(null);
    const idRef = useRef(formik.values.id);
    const blockSaveRef = useRef(false);
    const variablesRef = useRef(variables);
    const inDifferenceRef = useRef(false);
    const checkpointContentRef = useRef(null);
    const nextCheckpointIdRef = useRef(null);

    const [commentButtonPos, setCommentButtonPos] = useState(null);
    const [tableModal, setTableModal] = useState(null);
    const [lastSelection, setLastSelection] = useState(null);

    const handleDocUpdateRef = useRef(null);
    const editorRef = useRef(null);

    const debouncedOnUpdate = useRef(debounce(() => handleDocUpdateRef.current({editor: editorRef.current}), 800)).current;
    const editor = useDocumentEditor(setTableModal, blockSaveRef, debouncedOnUpdate, variablesRef, setCommentButtonPos, setLastSelection);
    editorRef.current = editor;

    const handleDocUpdate = ({editor, content}) => {
        const json = content ?? editor.getJSON();
        const cleanContent = cleanDiffDocument(json);
        const currentCheckpointId = checkpointIdRef.current;
        const currentCheckpointContent = checkpointContentRef.current;
        const isInDifference = inDifferenceRef.current;

        const checkpointPayload = {
            id: currentCheckpointId,
            content: cleanContent,
        }

        const updateCheckpointContent = (checkpointContent) => {
            setCheckpointsList(prev => prev.map(checkpoint => checkpoint.id === currentCheckpointId
                ? {...checkpoint, content: checkpointContent}
                : checkpoint
            ));
        }

        if (isInDifference) {
            const changedNodes = getChangedNodes(currentCheckpointContent, json);

            DocumentActions.get(idRef.current, (response) => {
                const mergedDoc = getMergedDoc(
                    response.content,
                    json,
                    currentCheckpointContent,
                    changedNodes
                );
                const cleanMergedDoc = cleanDiffDocument(mergedDoc);

                DocumentActions.save({
                    id: idRef.current,
                    name: formik.values.name,
                    color: formik.values.color,
                    content: cleanMergedDoc,
                    checkpoint: checkpointPayload
                }, () => {})

                updateCheckpointContent(cleanMergedDoc);
            })

            return null;
        }

        DocumentActions.save( {
            id: idRef.current,
            content: cleanContent,
            checkpoint: checkpointPayload,
            name: formik.values.name,
            color: formik.values.color
        }, () => {});

        updateCheckpointContent(cleanContent);

        return cleanContent;
    };
    handleDocUpdateRef.current = handleDocUpdate;

    useEffect(() => {
        variablesRef.current = variables;
    }, [variables]);

    useEffect(() => {
        return () => {
            debouncedOnUpdate.cancel();
            if (blockSaveRef.current || inDifferenceRef.current) return;

            // Save on unmount in case debounce is still pending
            handleDocUpdateRef.current({editor: editorRef.current});
        }
    }, []);

    useEffect(() => {
        const {id, content, checkpoints} = formik.values;

        if (!formik.values.id) return;

        idRef.current = id;
        checkpointIdRef.current = checkpoints[0].id;
        editor.commands.setContent(content);

    }, [formik.values]);

    const handleShowEditor = ({content, editable, diffMode, checkpointContent, checkpointId, nextCheckpointId}) => {
        inDifferenceRef.current = diffMode;
        checkpointIdRef.current = checkpointId;
        checkpointContentRef.current = checkpointContent;
        nextCheckpointIdRef.current = nextCheckpointId;


        blockSaveRef.current = true;
        editor.setEditable(editable);
        editor.commands.setContent(content);

        if (editable) {
            requestAnimationFrame(() => {
                blockSaveRef.current = false;
            });
        } else {
            editor.chain().focus().setColor("#3b3b3b").run();

                editor.view.dispatch(
                    editor.view.state.tr
                        .setStoredMarks([])
                        .setSelection(TextSelection.create(editor.view.state.doc, editor.view.state.selection.from))
                );
        }
    }

    const flushEditorChanges = () => debouncedOnUpdate.flush?.();

    return (
        <>
            <Toolbar editor={editor}/>

            <div className="editor-wrapper">
                <Checkpoints onShowEditor={handleShowEditor} flushEditorChanges={flushEditorChanges}/>


                <EditorContent
                    editor={editor}
                    spellCheck={false}
                />

                <Comments
                    commentButtonPos={commentButtonPos}
                    checkpointId={checkpointIdRef.current}
                    nextCheckpointId={nextCheckpointIdRef.current}
                    documentId={formik.values.id}
                    editor={editor}
                    lastSelection={lastSelection}
                />

                {tableModal &&
                    <TableModal
                        editor={editor}
                        initialData={tableModal}
                        onClose={() => setTableModal(null)}
                    />
                }
            </div>
        </>
    )
}

export default DocumentEditor;